import { css } from "lit";

/**
 * Shared :host styles for page components.
 * Provides base layout with padding - centering is handled by main in djkollektiv-app.
 */
export const PageHostStyles = css`
  :host {
    display: block;
    padding: var(--pd-spacing-lg) 0 var(--pd-spacing-lg) 0;
  }
`;

export const PageFlexHostStyles = css`
  :host {
    display: flex;
    padding: var(--pd-spacing-lg) 0 var(--pd-spacing-lg) 0;
    flex-direction: column;
    gap: var(--pd-spacing-lg);
  }
`;

/**
 * Shared header recipe for detail pages (request + location):
 * content-title with an inline badge chip, muted subtitle below.
 * The title's own top margin (DjkAbstractContent) clears the absolutely
 * positioned top-content-bar of djk-content-box — layouts that wrap the
 * title in a flex row must move that clearance onto the row instead.
 */
export const DetailHeaderStyles = css`
  h2.content-title {
    margin-bottom: var(--pd-spacing-xs, 0.25rem);
  }

  .header-subtitle {
    margin: 0 0 var(--pd-spacing-md, 1rem) 0;
    font-size: 0.9rem;
    color: var(--pd-default-font-light-col, #6b7280);
  }

  /* Badge chip inside the title (e.g. Extern/Intern, Daten ungeprüft). */
  .title-badge {
    display: inline-block;
    margin-left: var(--pd-spacing-sm, 0.5rem);
    padding: 0.15rem 0.5rem;
    font-size: 0.75rem;
    font-weight: 500;
    vertical-align: middle;
    border-radius: var(--pd-radius-sm, 0.25rem);
    background: var(--pd-default-light-col, #f3f4f6);
    color: var(--pd-default-font-light-col, #6b7280);
  }

  .title-badge--warn {
    background: var(--pd-warning-light-col, #fdf0dc);
    color: var(--pd-warning-col, #a8690f);
  }
`;

/**
 * Shared white info boxes of the detail pages (request: Kontakt/Event,
 * location: Meta/Ansprechpartner): two-column grid above the status
 * box, stacking on mobile.
 */
export const InfoBoxStyles = css`
  .info-boxes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--pd-spacing-md, 1rem);
    margin-bottom: var(--pd-spacing-md, 1rem);
  }

  @media (max-width: 768px) {
    .info-boxes {
      grid-template-columns: 1fr;
    }
  }

  .info-box {
    display: flex;
    flex-direction: column;
    gap: var(--pd-spacing-sm, 0.5rem);
    padding: var(--pd-spacing-md, 1rem);
    background: var(--pd-default-bg-col, #fefefe);
    border: 1px solid var(--pd-default-light-col, #e0e0e0);
    border-radius: var(--pd-radius-md, 0.5rem);
  }

  .info-box__title {
    font-family: var(--pd-default-font-title-family, inherit);
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--pd-default-font-title-col, #0a3a48);
    margin-bottom: var(--pd-spacing-xs, 0.25rem);
  }

  .info-box__row {
    display: flex;
    align-items: center;
    gap: var(--pd-spacing-sm, 0.5rem);
    font-size: 0.95rem;
    color: var(--pd-default-font-col);
  }

  .info-box__row a {
    color: var(--pd-default-col, #067394);
    text-decoration: none;
  }

  .info-box__row a:hover {
    text-decoration: underline;
  }

  .info-box__label {
    color: var(--pd-default-font-light-col, #6b7280);
    min-width: 5rem;
  }

  .info-box__icon {
    width: 1.2rem;
    text-align: center;
  }

  .info-box__badge {
    display: inline-block;
    padding: 0.2rem 0.5rem;
    font-size: 0.85rem;
    font-weight: 500;
    background: var(--pd-default-light-col, #f3f4f6);
    border-radius: var(--pd-radius-sm, 0.25rem);
    color: var(--pd-default-font-col);
  }
`;

/**
 * Shared grey status box of the detail pages (request + location):
 * state lines (label + value), inline text actions and a small-button
 * row for the primary state action. State lives HERE, never as a badge
 * in the title (title chips are reserved for immutable origin/type).
 */
export const StatusBoxStyles = css`
  .status-box {
    display: flex;
    flex-direction: column;
    gap: var(--pd-spacing-xs, 0.25rem);
    padding: var(--pd-spacing-sm, 0.5rem) var(--pd-spacing-md, 1rem);
    background: var(--pd-default-lightest-col, #f9fafb);
    border-radius: var(--pd-radius-sm, 0.25rem);
    font-size: 0.95rem;
    margin-bottom: var(--pd-spacing-lg, 2rem);
  }

  .status-line {
    display: flex;
    align-items: center;
    gap: var(--pd-spacing-sm, 0.5rem);
  }

  .status-line__label {
    color: var(--pd-default-font-light-col, #6b7280);
  }

  .status-line__value {
    font-weight: 500;
    color: var(--pd-default-font-title-col, #0a3a48);
  }

  .status-line__action {
    font-size: 0.85rem;
    color: var(--pd-default-col, #067394);
    cursor: pointer;
    text-decoration: underline;
  }

  .status-line__action:hover {
    color: var(--pd-default-dark-col, #045a73);
  }

  .status-buttons {
    display: flex;
    gap: var(--pd-spacing-sm, 0.5rem);
    margin-top: var(--pd-spacing-xs, 0.25rem);
  }
`;

/**
 * Weiterführender Link unter einer Liste: rechtsbündig, ohne Fläche, Pfeil
 * hinter dem Text. Die gefüllte Fläche bleibt den Accordions vorbehalten —
 * zwei gleich aussehende Balken mit verschiedener Bedeutung wären eine Falle.
 *
 * Bleibt ein pd-button (Fokus, Tastatur, Screenreader) und behält eine
 * unsichtbare Antippfläche von 44px Höhe fürs Handy.
 */
export const ListLinkStyles = css`
  .list-link {
    display: flex;
    justify-content: flex-end;
  }

  .list-link pd-button::part(button) {
    min-height: 44px;
    height: auto;
    padding: 0 0.25rem;
    background: none;
    border: 0;
    font-size: 0.8rem;
    color: var(--pd-default-font-link-col, var(--pd-default-col, #067394));
  }
`;

export const AppHomeSharedStyles = css`
  h1,
  h2,
  h3,
  h4 {
    color: var(--pd-default-font-title-col);
    font-family: var(--pd-default-font-title-family);
    margin-bottom: 0.5rem;
    margin-top: 2.5rem;
  }

  h1 {
    font-size: calc(1em + 2.5vmin);
  }

  h2 {
    font-size: calc(1em + 2vmin);
  }

  h3 {
    font-size: calc(0.8em + 1.8vmin);
  }

  p {
    font-family: var(--pd-default-font-content-family);
    line-height: 1.4em;
  }

  .no-padding-h {
    margin: 0;
    padding: 0;
  }

  .link {
    color: var(--pd-default-font-link-col, blue);
    font-family: var(--pd-default-font-link-family);
    font-size: var(--pd-default-font-link-size, 1em);
  }

  .link:hover {
    color: var(--pd-default-font-link-col-hover, purple);
    cursor: pointer;
  }
`;
